import { EntityType } from "namirasoft-expert";
import { NamirasoftInfraServer } from "namirasoft-infra";
import { Component, createRef } from "react";
import { InfraEntity, listInfraEntities } from "../listInfraEntities";
import Styles from "./MentionTextArea.module.css";

export interface Mention
{
    uid: string;
    entity_type: EntityType;
    entity_id?: string;
    name: string;
    label: string;
}

export interface MentionTextAreaValue
{
    text: string;
    mentions: Mention[];
}

interface MentionTextAreaProps
{
    infra: NamirasoftInfraServer;
    placeholder?: string;
    onSubmit?: () => void;
}

type PopupStage = "type" | "entity";

interface MentionTextAreaState
{
    popupOpen: boolean;
    popupStage: PopupStage;
    popupQuery: string;
    popupType?: EntityType;
    popupIndex: number;
    entitiesLoading: boolean;
    entities: InfraEntity[];
    popupPlacement: "above" | "below";
}

const ENTITY_TYPES: EntityType[] = [
    EntityType.Cloud,
    EntityType.Server,
    EntityType.Kubernetes,
    EntityType.Container,
    EntityType.Database,
    EntityType.Cache,
    EntityType.Messaging,
    EntityType.Streaming,
    EntityType.Metric,
];

const TRIGGER_CHARS = new Set(["@", "/"]);
const ZWSP = "​";

class MentionTextArea extends Component<MentionTextAreaProps, MentionTextAreaState>
{
    private editorRef = createRef<HTMLDivElement>();
    private listRef = createRef<HTMLDivElement>();
    private triggerNode: Text | null = null;
    private triggerOffset = 0;
    private uidCounter = 0;

    constructor(props: MentionTextAreaProps)
    {
        super(props);
        this.state = {
            popupOpen: false,
            popupStage: "type",
            popupQuery: "",
            popupIndex: 0,
            entitiesLoading: false,
            entities: [],
            popupPlacement: "above",
        };
        this.handleInput = this.handleInput.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleEditorClick = this.handleEditorClick.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.updatePopupPlacement = this.updatePopupPlacement.bind(this);
    }

    override componentDidUpdate(_prev: MentionTextAreaProps, prevState: MentionTextAreaState)
    {
        if (this.state.popupOpen && !prevState.popupOpen)
        {
            this.updatePopupPlacement();
            window.addEventListener("scroll", this.updatePopupPlacement, true);
            window.addEventListener("resize", this.updatePopupPlacement);
        }
        else if (!this.state.popupOpen && prevState.popupOpen)
        {
            window.removeEventListener("scroll", this.updatePopupPlacement, true);
            window.removeEventListener("resize", this.updatePopupPlacement);
        }

        if (this.state.popupOpen && (prevState.popupIndex !== this.state.popupIndex || prevState.popupStage !== this.state.popupStage))
        {
            const list = this.listRef.current;
            if (!list)
                return;
            const active = list.querySelector<HTMLElement>(`[data-popup-index="${this.state.popupIndex}"]`);
            if (!active)
                return;
            const top = active.offsetTop;
            const bottom = top + active.offsetHeight;
            if (top < list.scrollTop)
                list.scrollTop = top;
            else if (bottom > list.scrollTop + list.clientHeight)
                list.scrollTop = bottom - list.clientHeight;
        }
    }

    override componentWillUnmount()
    {
        window.removeEventListener("scroll", this.updatePopupPlacement, true);
        window.removeEventListener("resize", this.updatePopupPlacement);
    }

    private updatePopupPlacement()
    {
        const editor = this.editorRef.current;
        if (!editor)
            return;
        const rect = editor.getBoundingClientRect();
        const popupMaxHeight = 16 * 16;
        const margin = 16;
        const placement: "above" | "below" = rect.top < popupMaxHeight + margin ? "below" : "above";
        if (placement !== this.state.popupPlacement)
            this.setState({ popupPlacement: placement });
    }

    public getValue(): MentionTextAreaValue
    {
        const editor = this.editorRef.current;
        if (!editor)
            return { text: "", mentions: [] };

        const mentions: Mention[] = [];
        let text = "";

        const walk = (node: Node) =>
        {
            if (node.nodeType === Node.TEXT_NODE)
            {
                text += (node.textContent ?? "").replace(new RegExp(ZWSP, "g"), "");
                return;
            }
            if (node.nodeType === Node.ELEMENT_NODE)
            {
                const el = node as HTMLElement;
                if (el.dataset.mentionUid)
                {
                    const entity_type = el.dataset.entityType as EntityType;
                    const entity_id = el.dataset.entityId || undefined;
                    const name = el.dataset.name ?? "";
                    const label = el.dataset.label ?? "";
                    mentions.push({
                        uid: el.dataset.mentionUid,
                        entity_type,
                        entity_id,
                        name,
                        label,
                    });
                    text += `@{${entity_type} ID:${entity_id ?? ""} Name:${name}}`;
                    return;
                }
                if (el.tagName === "BR")
                {
                    text += "\n";
                    return;
                }
                if (el.tagName === "DIV" && text.length > 0 && !text.endsWith("\n"))
                    text += "\n";
                el.childNodes.forEach(walk);
            }
        };

        editor.childNodes.forEach(walk);
        return { text: text.trim(), mentions };
    }

    public clear()
    {
        const editor = this.editorRef.current;
        if (editor)
            editor.innerHTML = "";
        this.closePopup();
        this.forceUpdate();
    }

    public focus()
    {
        this.editorRef.current?.focus();
    }

    private generateUid(): string
    {
        this.uidCounter += 1;
        return `m_${Date.now()}_${this.uidCounter}`;
    }

    private closePopup()
    {
        this.triggerNode = null;
        this.triggerOffset = 0;
        this.setState({
            popupOpen: false,
            popupStage: "type",
            popupQuery: "",
            popupType: undefined,
            popupIndex: 0,
            entities: [],
        });
    }

    private handleInput()
    {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
        {
            this.closePopup();
            return;
        }
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const offset = range.startOffset;

        if (this.state.popupOpen && this.state.popupStage === "type")
        {
            if (this.triggerNode && node === this.triggerNode)
            {
                const text = this.triggerNode.textContent ?? "";
                const trigger = text[this.triggerOffset];
                if (trigger && TRIGGER_CHARS.has(trigger) && offset > this.triggerOffset)
                {
                    const query = text.slice(this.triggerOffset + 1, offset);
                    if (/\s/.test(query))
                    {
                        this.closePopup();
                        return;
                    }
                    this.setState({ popupQuery: query, popupIndex: 0 });
                    return;
                }
            }
            this.closePopup();
            return;
        }

        if (node.nodeType === Node.TEXT_NODE && offset > 0)
        {
            const textNode = node as Text;
            const ch = (textNode.textContent ?? "")[offset - 1];
            if (ch && TRIGGER_CHARS.has(ch))
            {
                const before = (textNode.textContent ?? "")[offset - 2];
                if (!before || /\s/.test(before))
                {
                    this.triggerNode = textNode;
                    this.triggerOffset = offset - 1;
                    this.setState({
                        popupOpen: true,
                        popupStage: "type",
                        popupQuery: "",
                        popupType: undefined,
                        popupIndex: 0,
                    });
                    return;
                }
            }
        }
    }

    private handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>)
    {
        if (this.state.popupOpen)
        {
            const items = this.getCurrentPopupItems();
            if (e.key === "ArrowDown")
            {
                e.preventDefault();
                this.setState((prev) => ({ popupIndex: items.length === 0 ? 0 : (prev.popupIndex + 1) % items.length }));
                return;
            }
            if (e.key === "ArrowUp")
            {
                e.preventDefault();
                this.setState((prev) => ({ popupIndex: items.length === 0 ? 0 : (prev.popupIndex - 1 + items.length) % items.length }));
                return;
            }
            if (e.key === "Escape")
            {
                e.preventDefault();
                this.closePopup();
                return;
            }
            if (e.key === "Enter" && !e.shiftKey)
            {
                e.preventDefault();
                if (items.length > 0)
                    this.selectPopupItem(items[this.state.popupIndex] ?? items[0]);
                return;
            }
            if (e.key === "Tab")
            {
                e.preventDefault();
                if (items.length > 0)
                    this.selectPopupItem(items[this.state.popupIndex] ?? items[0]);
                return;
            }
        }
        else if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey)
        {
            e.preventDefault();
            this.props.onSubmit?.();
            return;
        }
    }

    private getCurrentPopupItems(): PopupItem[]
    {
        const { popupStage, popupQuery, popupType, entities } = this.state;
        if (popupStage === "type")
        {
            const q = popupQuery.toLowerCase();
            return ENTITY_TYPES.filter(t => t.toLowerCase().startsWith(q)).map(t => ({
                kind: "type",
                entity_type: t,
                title: t,
            }));
        }
        if (popupStage === "entity" && popupType)
        {
            const q = popupQuery.toLowerCase();
            const all: PopupItem = { kind: "entity-all", entity_type: popupType, title: `All ${popupType}` };
            const list: PopupItem[] = entities
                .filter(e =>
                {
                    if (!q)
                        return true;
                    const name = (e.name ?? e.id ?? "").toLowerCase();
                    return name.includes(q);
                })
                .map(e => ({
                    kind: "entity",
                    entity_type: popupType,
                    entity_id: e.id,
                    title: e.name ?? e.id,
                    subtitle: e.description ?? undefined,
                }));
            return [all, ...list];
        }
        return [];
    }

    private async openEntityStage(entity_type: EntityType)
    {
        this.setState({
            popupStage: "entity",
            popupType: entity_type,
            popupQuery: "",
            popupIndex: 0,
            entitiesLoading: true,
            entities: [],
        });
        try
        {
            const response = await listInfraEntities(
                this.props.infra,
                entity_type,
                [],
                1,
                100,
                [],
            );
            this.setState({ entities: response.rows, entitiesLoading: false });
        }
        catch
        {
            this.setState({ entitiesLoading: false });
        }
    }

    private selectPopupItem(item: PopupItem)
    {
        if (item.kind === "type")
        {
            this.openEntityStage(item.entity_type);
            return;
        }
        const name = item.kind === "entity-all" ? "All" : item.title;
        const label = `${item.entity_type}: ${name}`;
        this.insertChip({
            uid: this.generateUid(),
            entity_type: item.entity_type,
            entity_id: item.kind === "entity" ? item.entity_id : undefined,
            name,
            label,
        });
    }

    private insertChip(mention: Mention)
    {
        const editor = this.editorRef.current;
        if (!editor || !this.triggerNode)
        {
            this.closePopup();
            return;
        }

        const range = document.createRange();
        range.setStart(this.triggerNode, this.triggerOffset);
        const sel = window.getSelection();
        const endOffset = sel && sel.rangeCount > 0 && sel.getRangeAt(0).startContainer === this.triggerNode
            ? sel.getRangeAt(0).startOffset
            : (this.triggerNode.textContent ?? "").length;
        range.setEnd(this.triggerNode, endOffset);
        range.deleteContents();

        const chip = document.createElement("span");
        chip.className = Styles.ns_mention_chip;
        chip.contentEditable = "false";
        chip.dataset.mentionUid = mention.uid;
        chip.dataset.entityType = mention.entity_type;
        chip.dataset.entityId = mention.entity_id ?? "";
        chip.dataset.name = mention.name;
        chip.dataset.label = mention.label;

        const labelSpan = document.createElement("span");
        labelSpan.textContent = `@{${mention.entity_type} ID:${mention.entity_id ?? ""} Name:${mention.name}}`;
        chip.appendChild(labelSpan);

        const removeBtn = document.createElement("span");
        removeBtn.className = Styles.ns_mention_chip_remove;
        removeBtn.dataset.removeMention = mention.uid;
        removeBtn.textContent = "×";
        removeBtn.setAttribute("role", "button");
        removeBtn.setAttribute("aria-label", "Remove mention");
        chip.appendChild(removeBtn);

        range.insertNode(chip);

        const trailing = document.createTextNode(" ");
        chip.parentNode?.insertBefore(trailing, chip.nextSibling);

        const newRange = document.createRange();
        newRange.setStart(trailing, trailing.length);
        newRange.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(newRange);

        this.closePopup();
        this.forceUpdate();
        editor.focus();
    }

    private handleEditorClick(e: React.MouseEvent<HTMLDivElement>)
    {
        const target = e.target as HTMLElement;
        const removeUid = target.dataset?.removeMention;
        if (removeUid)
        {
            e.preventDefault();
            const editor = this.editorRef.current;
            if (!editor)
                return;
            const chip = editor.querySelector(`[data-mention-uid="${removeUid}"]`);
            chip?.parentNode?.removeChild(chip);
            this.forceUpdate();
        }
    }

    private handleBlur()
    {
        window.setTimeout(() =>
        {
            const editor = this.editorRef.current;
            const active = document.activeElement;
            if (editor && active && (editor === active || editor.contains(active)))
                return;
            if (active && (active as HTMLElement).closest?.(`.${Styles.ns_mention_popup}`))
                return;
            this.closePopup();
        }, 120);
    }

    override render()
    {
        const { popupOpen, popupStage, entitiesLoading, popupType, popupIndex, popupPlacement } = this.state;
        const items = this.getCurrentPopupItems();

        return (
            <div className={Styles.ns_mention_wrapper}>
                <div
                    ref={this.editorRef}
                    className={Styles.ns_mention_editor}
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    aria-multiline="true"
                    data-placeholder={this.props.placeholder ?? "Type @ or / to mention an entity..."}
                    onInput={this.handleInput}
                    onKeyDown={this.handleKeyDown}
                    onClick={this.handleEditorClick}
                    onBlur={this.handleBlur}
                />
                {
                    popupOpen &&
                    <div className={`${Styles.ns_mention_popup} ${popupPlacement === "below" ? Styles.ns_mention_popup_below : ""}`} onMouseDown={(e) => e.preventDefault()}>
                        <div className={Styles.ns_mention_popup_header}>
                            {
                                popupStage === "entity" &&
                                <button
                                    type="button"
                                    className={Styles.ns_mention_popup_back}
                                    onClick={() => this.setState({
                                        popupStage: "type",
                                        popupQuery: "",
                                        popupType: undefined,
                                        popupIndex: 0,
                                        entities: [],
                                    })}
                                >
                                    ‹ Back
                                </button>
                            }
                            <span>
                                {popupStage === "type" ? "Select entity type" : `Select ${popupType}`}
                            </span>
                        </div>
                        <div className={Styles.ns_mention_popup_list} ref={this.listRef}>
                            {
                                popupStage === "entity" && entitiesLoading &&
                                <div className={Styles.ns_mention_popup_empty}>Loading...</div>
                            }
                            {
                                !entitiesLoading && items.length === 0 &&
                                <div className={Styles.ns_mention_popup_empty}>No matches</div>
                            }
                            {
                                !entitiesLoading && items.map((item, i) => (
                                    <div
                                        key={`${item.kind}-${item.entity_type}-${"entity_id" in item ? item.entity_id : ""}-${i}`}
                                        data-popup-index={i}
                                        className={`${Styles.ns_mention_popup_item} ${i === popupIndex ? Styles.ns_active : ""}`}
                                        onMouseEnter={() => this.setState({ popupIndex: i })}
                                        onClick={() => this.selectPopupItem(item)}
                                    >
                                        <span className={Styles.ns_mention_popup_item_title}>{item.title}</span>
                                        {
                                            "subtitle" in item && item.subtitle &&
                                            <span className={Styles.ns_mention_popup_item_subtitle}>{item.subtitle}</span>
                                        }
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                }
            </div>
        );
    }
}

type PopupItem =
    | { kind: "type"; entity_type: EntityType; title: string }
    | { kind: "entity-all"; entity_type: EntityType; title: string }
    | { kind: "entity"; entity_type: EntityType; entity_id: string; title: string; subtitle?: string };

export default MentionTextArea;